import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { useItemCache } from '../context/ItemCacheContext';
import useItems from './useItems';
import { useDocumentLineItemWorkOrder } from './useDocumentLineItemWorkOrder';
import { useDocumentLineLookups } from './useDocumentLineLookups';
import { DocumentLineItemWorkOrder, Item } from '../types/Types';
import { parseDecimalInput, formatQtyForDisplay, formatMoney, formatUnitPrice } from '../utils/decimalInput';

interface UseNestedDocumentRowsParams {
  documentId: string;
  inputRefs: React.MutableRefObject<(HTMLInputElement | HTMLSelectElement | null)[][]>;
  resetTrigger?: number;
  batch?: number;
  documentCategory?: "material" | "non-material" | "planning" | "work-order";
  defaultPartner?: { _id: string; name?: string } | null;
  columnOrder: string[];
}

/**
 * Row CRUD (save) + server-sync + focus management for NestedDocumentTable.
 * Extracted 1:1 from NestedDocumentTable.tsx (CLAUDE.md rule 9 split) — no behaviour change.
 * Row selection/bulk-delete and the quantity modal are split out into
 * useNestedDocumentSelection.ts / useNestedDocumentQuantityModal.ts (composed in the
 * screen component) since those are separate UI flows; everything left here shares the
 * same localRows/savingRows state produced by the backend-sync effects below, so it stays
 * one hook (naturally large — see report).
 */
export function useNestedDocumentRows({
  documentId,
  inputRefs,
  resetTrigger,
  batch,
  documentCategory,
  defaultPartner,
  columnOrder,
}: UseNestedDocumentRowsParams) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { data: rows, loading, error, createDocumentLineItemWorkOrder, editDocumentLineItemWorkOrder, deleteDocumentLineItemWorkOrder, refetch } = useDocumentLineItemWorkOrder(documentId);
  const { getItemById } = useItems();
  const { initialize, getItem, addItemsToCache, preloadItemsForRows } = useItemCache();
  const { fetchItemsByIds } = useDocumentLineLookups();
  // --- State ---
  const [localRows, setLocalRows] = React.useState<DocumentLineItemWorkOrder[]>([]);
  // Item meta lookup for display-only fields (e.g. unit measurement) when backend returns only {_id, ident, name}
  const [itemMetaById, setItemMetaById] = React.useState<Record<string, any>>({});
  const [savingRows, setSavingRows] = React.useState<Set<number>>(new Set());
  // --- Misc ---
  const initRef = useRef(false);
  const buildDefaultWarehouse = React.useCallback(() => {
    if (!defaultPartner || !defaultPartner._id) return null;
    return { _id: defaultPartner._id, name: defaultPartner.name || '' };
  }, [defaultPartner]);

  const createEmptyRow = React.useCallback((): DocumentLineItemWorkOrder => ({
    document: documentId,
    item: null,
    warehouse: buildDefaultWarehouse(),
    purchasePrice: '',
    valuationPrice: '',
    vat: '',
    forPayment: '',
    quantity: '',
    quantityOS: '',
    quantityWorkOrder: '',
    serial: '',
    deadline: '',
    priceValue: ''
  }), [documentId, buildDefaultWarehouse]);

  useEffect(() => {
    if (getItemById && !initRef.current) {
      initialize(getItemById);
      initRef.current = true;
    }
  }, [initialize, getItemById]);

  // Refetch data when resetTrigger changes (after auto-population)
  useEffect(() => {
    if (resetTrigger && resetTrigger > 0 && documentId) {
      console.log('[NestedDocumentTable] resetTrigger changed, refetching data...');
      refetch();
    }
  }, [resetTrigger, documentId, refetch]);

  // Aggressively preload items when rows load to prevent slow CachedItemSelectByCode loading
  useEffect(() => {
    const preloadItemsForRows = async () => {
      if (rows && rows.length > 0) {
        // Extract all unique item IDs that need to be cached BEFORE components render
        const itemIds = rows
          .filter(row => row.item?._id)
          .map(row => row.item!._id)
          .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

        // If we have items to cache, bulk load them immediately
        if (itemIds.length > 0) {
          try {
            // Use the bulk-ids endpoint - ONE API call for ALL items!
            const items = await fetchItemsByIds(itemIds);
            if (items.length > 0) {
              // Add all items to cache immediately BEFORE table renders
              const normalized = items.map((item: any) => ({
                ...item,
                _id: item._id,
                ident: item.ident || '',
                name: item.name || '',
                unitMeasurment: item.unitMeasurment || item.unitMeasurement || ''
              } as Item));
              addItemsToCache(normalized);

              // Also keep a lightweight lookup for render-time fallbacks (unit measurement, etc.)
              setItemMetaById((prev) => {
                const next = { ...prev };
                for (const it of normalized) next[it._id] = it;
                return next;
              });
            }
          } catch (error) {
            console.warn('Failed to preload items for fast loading:', error);
          }
        }
      }
    };

    preloadItemsForRows();
  }, [rows, addItemsToCache, fetchItemsByIds]);

  // Sync backend data to local state
  useEffect(() => {
    // Always sync rows from backend when they change
    if (rows && Array.isArray(rows)) {
      if (rows.length > 0) {
        // We have data from backend - hydrate rows that don't have warehouse info yet (new unsaved rows)
        const hydratedRows = rows.map((row) => {
          const warehouse = row.warehouse;
          const hasWarehouse = warehouse && (typeof warehouse === 'object' ? warehouse._id : warehouse);
          const base = !hasWarehouse && !row._id && defaultPartner
            ? { ...row, warehouse: buildDefaultWarehouse() }
            : { ...row };
          // Internal production transfer — DDV never applies
          if (documentCategory === 'work-order') base.vat = 0;
          const asPrice = (value: unknown) => {
            const n = parseDecimalInput(value as string | number);
            if (n === null || n === 0) return '';
            return formatUnitPrice(n);
          };
          const asQty = (value: unknown) => formatQtyForDisplay(parseDecimalInput(value as string | number));
          const asMoney = (value: unknown) => {
            const n = parseDecimalInput(value as string | number);
            if (n === null) return '';
            return formatMoney(n);
          };
          return {
            ...base,
            purchasePrice: asPrice(base.purchasePrice),
            valuationPrice: asPrice(base.valuationPrice),
            quantity: asQty(base.quantity),
            quantityOS: asQty(base.quantityOS),
            quantityWorkOrder: asQty(base.quantityWorkOrder),
            priceValue: asMoney(base.priceValue),
          };
        });
        setLocalRows(hydratedRows);
      } else if (documentId) {
        // If we have a document ID but no rows yet, create an empty row for data entry
        setLocalRows([createEmptyRow()]);
      } else {
        // No document ID means new document - table should be completely empty
        setLocalRows([]);
      }
    } else if (documentId) {
      // If we have a document ID but rows haven't loaded yet (rows is undefined/null), create an empty row
      // This handles the initial state before data loads
      setLocalRows([createEmptyRow()]);
    } else {
      // No document ID and no rows - empty state
      setLocalRows([]);
    }
  }, [rows, documentId, createEmptyRow, buildDefaultWarehouse, defaultPartner]);

  // Calculate quantityOS when batch changes or rows load
  useEffect(() => {
    if (batch !== undefined && batch > 0 && localRows.length > 0) {
      const parseNumber = (value?: string | number): number => parseDecimalInput(value) ?? 0;

      const updatedRows = localRows.map(row => {
        const ingredientQty = parseNumber(row.quantity);
        const calculatedOS = ingredientQty * batch;
        return {
          ...row,
          quantityOS: formatQtyForDisplay(calculatedOS, { showZero: true })
        };
      });

      setLocalRows(updatedRows);
    }
  }, [batch]);

  // Note: quantityWorkOrder calculation is now done by the backend FEFO recalculation endpoint
  // When user clicks "Apply" button, it calls /api/document-line-item-work-order/recalculate-fefo
  // which splits rows across stock batches using FEFO and sets correct serial/deadline

  // Focus management function similar to PartnerRoleInfoTable
  const focusLastRow = () => {
    setTimeout(() => {
      if (localRows.length > 0) {
        const lastRowIndex = localRows.length - 1;
        const priceColumnIndex = columnOrder.indexOf('purchasePrice') + 2; // +2 for checkbox and loading columns
        const priceInput = inputRefs.current[lastRowIndex]?.[priceColumnIndex];
        if (priceInput) {
          priceInput.focus();
          if (priceInput instanceof HTMLInputElement) {
            priceInput.select();
          }
        }
      }
    }, 0);
  };

  // Focus purchasePrice column of last row when rows change
  React.useEffect(() => {
    // Always focus after setting rows (whether data exists or not)
    setTimeout(() => focusLastRow(), 100);
  }, [localRows.length, columnOrder]);

  // Focus on last row whenever rows change or documentId changes
  React.useEffect(() => {
    // Add a longer delay to ensure data is loaded and rendered
    setTimeout(() => {
      focusLastRow();
    }, 200);
  }, [localRows.length, documentId]);

  const parseNumber = (value?: string | number): number => parseDecimalInput(value) ?? 0;

  const formatPriceForSave = (value?: string | number) => {
    const num = parseDecimalInput(value);
    if (num === null || num === 0) return "";
    return formatUnitPrice(num);
  };

  const formatQtyForSave = (value?: string | number) => formatQtyForDisplay(parseDecimalInput(value));

  // Calculate priceValue (purchasePrice × quantity)
  const calculatePriceValue = (purchasePrice: string | number, quantity: string | number): string => {
    const priceStr = String(purchasePrice || '0');
    const quantityStr = String(quantity || '0');

    const priceNum = parseNumber(priceStr);
    const quantityNum = parseNumber(quantityStr);

    if (isNaN(priceNum) || isNaN(quantityNum)) return '';

    const result = priceNum * quantityNum;
    return formatMoney(result);
  };

  // Save row to backend
  const handleSaveRow = async (rowIdx: number) => {
    const row = localRows[rowIdx];
    // Check required fields
    if (!row.item?._id) {
      return;
    }
    // Check required warehouse field (warehouse per row)
    const warehouseId = typeof row.warehouse === 'object' ? row.warehouse?._id : row.warehouse;
    if (!warehouseId) {
      showToast(t('errors.warehouseRequiredPerItem') || 'Warehouse is required for each item', 'error');
      return;
    }
    // Set loading state for this row
    setSavingRows(prev => new Set(prev).add(rowIdx));

    const payload: any = {
      document: documentId,
      item: row.item._id,
      warehouse: warehouseId, // Use the warehouse from this specific row
      purchasePrice: parseNumber(row.purchasePrice) || 0,
      valuationPrice: parseNumber(row.valuationPrice) || 0,
      quantity: parseNumber(row.quantity) || 0,
      quantityOS: parseNumber(row.quantityOS) || 0,
      quantityWorkOrder: parseNumber(row.quantityWorkOrder) || 0,
      serial: row.serial || '',
      deadline: row.deadline || '',
      priceValue: parseNumber(row.priceValue) || 0,
    };

    try {
      if (row._id) {
        await editDocumentLineItemWorkOrder(row._id, payload);
      } else {
        const saved = await createDocumentLineItemWorkOrder(payload);
        // Update local row with the saved ID
        const updated = [...localRows];
        updated[rowIdx] = { ...updated[rowIdx], _id: saved._id };
        setLocalRows(updated);
      }

      // After successful save, format the saved row to show 0.00 for empty fields
      const updated = [...localRows];
      const originalPrice = updated[rowIdx].purchasePrice;
      const originalQuantity = updated[rowIdx].quantity;
      updated[rowIdx] = {
        ...updated[rowIdx],
        purchasePrice: formatPriceForSave(originalPrice),
        quantity: formatQtyForSave(originalQuantity),
        // Recalculate priceValue with original values before formatting
        priceValue: calculatePriceValue(originalPrice, originalQuantity)
      };
      setLocalRows(updated);

      // Focus last row after successful save
      focusLastRow();
    } catch (err) {
      // error is handled by hook
    } finally {
      setSavingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowIdx);
        return newSet;
      });
    }
  };

  // Save all rows that have data (both new and edited)
  const handleSaveAllPendingRows = async (): Promise<boolean> => {
    // Find all rows that have data - both new (no _id) and edited (has _id)
    const rowsToSave = localRows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.item?._id); // Any row with an item should be saved

    if (rowsToSave.length === 0) return true;

    // Check if any row is missing warehouse
    const rowsWithoutWarehouse = rowsToSave.filter(({ row }) => {
      const warehouseId = typeof row.warehouse === 'object' ? row.warehouse?._id : row.warehouse;
      return !warehouseId;
    });
    if (rowsWithoutWarehouse.length > 0) {
      showToast(t('errors.warehouseRequiredPerItem') || `${rowsWithoutWarehouse.length} item(s) are missing warehouse assignment`, 'error');
      return false;
    }

    // Create a copy of localRows that we'll update
    let updatedRows = [...localRows];

    // Save all rows with data (both new and edited)
    let hadError = false;
    for (const { row, index } of rowsToSave) {
      setSavingRows(prev => new Set(prev).add(index));

      const warehouseId = typeof row.warehouse === 'object' ? row.warehouse?._id : row.warehouse;
      const payload: any = {
        document: documentId,
        item: row.item?._id,
        warehouse: warehouseId, // Use warehouse from each row
        purchasePrice: parseNumber(row.purchasePrice) || 0,
        valuationPrice: parseNumber(row.valuationPrice) || 0,
        quantity: parseNumber(row.quantity) || 0,
        quantityOS: parseNumber(row.quantityOS) || 0,
        quantityWorkOrder: parseNumber(row.quantityWorkOrder) || 0,
        serial: row.serial || '',
        deadline: row.deadline || '',
        priceValue: parseNumber(row.priceValue) || 0,
      };

      try {
        if (row._id) {
          // Edit existing row
          await editDocumentLineItemWorkOrder(row._id, payload);
          console.log(`NestedDocumentTable: Updated existing row ${row._id}`);
        } else {
          // Create new row
          const saved = await createDocumentLineItemWorkOrder(payload);
          // Update the local copy with saved ID
          updatedRows[index] = {
            ...updatedRows[index],
            _id: saved._id
          };
          console.log(`NestedDocumentTable: Created new row ${saved._id}`);
        }
      } catch (err) {
        hadError = true;
        console.error(`NestedDocumentTable: Failed to save row at index ${index}:`, err);
        // error is handled by hook
      } finally {
        setSavingRows(prev => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      }
    }

    // Update the state with saved data first
    setLocalRows(updatedRows);

    // Apply save formatting after a brief delay
    setTimeout(() => {
      setLocalRows(prevRows =>
        prevRows.map(row => {
          const originalPrice = row.purchasePrice;
          const originalQuantity = row.quantity;
          return {
            ...row,
            purchasePrice: formatPriceForSave(originalPrice),
            quantity: formatQtyForSave(originalQuantity),
            // Recalculate priceValue with original values before formatting
            priceValue: calculatePriceValue(originalPrice, originalQuantity)
          };
        })
      );

      // Focus on the last row after formatting is complete
      setTimeout(() => {
        focusLastRow();
      }, 50);
    }, 100);

    return !hadError;
  };

  return {
    localRows,
    setLocalRows,
    itemMetaById,
    savingRows,
    setSavingRows,
    loading,
    getItemById,
    refetch,
    editDocumentLineItemWorkOrder,
    createDocumentLineItemWorkOrder,
    deleteDocumentLineItemWorkOrder,
    createEmptyRow,
    focusLastRow,
    calculatePriceValue,
    handleSaveRow,
    handleSaveAllPendingRows,
  };
}

export default useNestedDocumentRows;
